import React, { useContext, useEffect, useState } from "react";
import { approveCheckout, cancelSampleCheckout, checkin, getCustomerUserInfo, getProductCheckoutOptions, guestCheckoutBatch, requestSampleCheckout, returnSample } from "../../Actions";
import { Autocomplete } from '@material-ui/lab';
import '../styles/Product.css';
import { Accordion, AccordionDetails, AccordionSummary, AppBar, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogContent, Divider, Drawer, FormControl, FormControlLabel, FormLabel, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, makeStyles, Radio, RadioGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography } from "@material-ui/core";
import { Close, CropFree, Email, ExpandLess, ExpandMore, Info, LibraryAddCheck, Person, Phone, Style } from "@material-ui/icons";
import { AlertContext } from "../../Context/AlertContextProvider";
import Scanner from "../LandingPage/Scanner/Scanner";

const header = ['Name', 'Location', 'Color/Option', 'Status', 'Days', 'Approve?'];
const drawerWidth = 320;

// A cart entry is one (product, sku) pair. The null sku — "General Product Sample" —
// gets its own sentinel so it can't silently collide with a real one.
const GENERAL_SAMPLE = 'GENERAL';
const cartKey = (productUUID, productSkuUUID) =>
  `${productUUID}::${productSkuUUID || GENERAL_SAMPLE}`;

// QR codes encode `${BASE_URL}/product/<product_uuid>?id=<print_queue_id>`. Pull the
// uuid back out of whatever the camera decoded. Anything that isn't a product link
// returns null so the caller can say so rather than firing a doomed lookup.
const parseScannedProductUUID = (scanned) => {
  if (!scanned || typeof scanned !== 'string') return null;

  const match = scanned.match(/\/product\/([A-Za-z0-9-]+)/);

  return match ? match[1] : null;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: 675,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    position: 'absolute',
    backgroundColor: '#142E3E',
    alignItems: 'center',
    width: '100%',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    position: 'absolute',
  },
  drawerContainer: {
    overflow: 'auto',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  headerText: {
    color: '#28C4FC !important',
    marginLeft: 100,
  },
  button: {
    backgroundColor: '#EEEEEE',
    color: '#ff682c',
    margin: 10,
  },
  tableHeader: {
    fontSize: 30,
    '@media (max-width: 1090px)': {
      fontSize: 15,
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '97%',
  },
  closeButton: {
    marginRight: 15,
  },
  autoComplete: {
    width: 170,
  },
}));

export default function SampleCheckoutDialog(props) {
  const { handleAlertOpen, setMessageType, setMessage } =
      useContext(AlertContext);
  const classes = useStyles();
  const [checkoutName, setCheckoutName] = useState({});
  const [checkoutEmail, setCheckoutEmail] = useState({});
  const [checkoutPhone, setCheckoutPhone] = useState({});
  const [tempName, setTempName] = useState(null);
  const [tempEmail, setTempEmail] = useState(null);
  const [tempPhone, setTempPhone] = useState(null);
  const [tempUserId, setTempUserId] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [reminderPreference, setReminderPreference] = useState('');

  // The samples going home with this customer — a set of (product, sku).
  const [cart, setCart] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The picker overlay. `picker` holds whichever product is being chosen from,
  // either this page's product or one just scanned; `pickerChecked` is the
  // in-progress selection within it.
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [picker, setPicker] = useState(null);
  const [pickerChecked, setPickerChecked] = useState({});
  const [pickerError, setPickerError] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);

  const handleCheckoutName = (e) => {
    setCheckoutName({ name: e });
  };

  const handleNameAutoComplete = (e, data) => {
    if (data === null) {
      setCheckoutName({ name: '' });
    } else {
      setIsDisabled(true);
      setTempUserId(data.user_id);
      setTempName({ name: data.name });
      setTempEmail({ email: data.email });
      setTempPhone({ phone: data.phone });
      setCheckoutName({ name: data.name });
      setCheckoutEmail({ email: data.email });
      setCheckoutPhone({ phone: data.phone });
      
      if (data.contact_by_email == 1 && data.contact_by_phone == 1) {
        setReminderPreference('2');
      } else if (data.contact_by_email == 1) {
        setReminderPreference('0');
      } else if (data.contact_by_phone == 1) {
        setReminderPreference('1');
      } else {
        setReminderPreference('');
      }
    }
  };

  const handleCheckoutEmail = (e) => {
    setCheckoutEmail({ email: e });
  };

  const handleEmailAutoComplete = (e, data) => {
    if (data === null) {
      setCheckoutEmail({ email: '' });
    } else {
      setIsDisabled(true);
      setTempUserId(data.user_id);
      setTempName({ name: data.name });
      setTempEmail({ email: data.email });
      setTempPhone({ phone: data.phone });
      setCheckoutName({ name: data.name });
      setCheckoutEmail({ email: data.email });
      setCheckoutPhone({ phone: data.phone });

      if (data.contact_by_email == 1 && data.contact_by_phone == 1) {
        setReminderPreference('2');
      } else if (data.contact_by_email == 1) {
        setReminderPreference('0');
      } else if (data.contact_by_phone == 1) {
        setReminderPreference('1');
      } else {
        setReminderPreference('');
      }
    }
  };

  const handleCheckoutPhone = (e) => {
    setCheckoutPhone({ phone: e });
  };

  const handlePhoneAutoComplete = (e, data) => {
    if (data === null) {
      setCheckoutPhone({ phone: '' });
    } else {
      setIsDisabled(true);
      setTempUserId(data.user_id);
      setTempName({ name: data.name });
      setTempEmail({ email: data.email });
      setTempPhone({ phone: data.phone });
      setCheckoutName({ name: data.name });
      setCheckoutEmail({ email: data.email });
      setCheckoutPhone({ phone: data.phone });

      if (data.contact_by_email == 1 && data.contact_by_phone == 1) {
        setReminderPreference('2');
      } else if (data.contact_by_email == 1) {
        setReminderPreference('0');
      } else if (data.contact_by_phone == 1) {
        setReminderPreference('1');
      } else {
        setReminderPreference('');
      }
    }
  };

  // Opens the picker on a product's SKU collection. Anything already in the cart
  // comes back ticked and locked, so a re-scan can only add what isn't taken yet.
  const openPicker = (productUUID, productName, options) => {
    setPicker({ productUUID, productName, options: options ?? [] });
    setPickerChecked({});
    setPickerError('');
    setIsScanning(false);
    setIsPickerOpen(true);
  };

  const openPickerForThisProduct = () => {
    openPicker(props.productId, props.productName, props.options);
  };

  const openScanner = () => {
    setPicker(null);
    setPickerChecked({});
    setPickerError('');
    setIsScanning(true);
    setIsPickerOpen(true);
  };

  const closePicker = () => {
    setIsPickerOpen(false);
    setIsScanning(false);
    setPicker(null);
    setPickerChecked({});
    setPickerError('');
  };

  const handleScan = (scannedUrl) => {
    const productUUID = parseScannedProductUUID(scannedUrl);

    setIsScanning(false);

    if (!productUUID) {
      setPickerError("That doesn't look like a product QR code. Try again.");
      return;
    }

    setIsLookingUp(true);
    getProductCheckoutOptions(productUUID)
      .then((result) => {
        const data = result.data;
        const name = [data.manufacturer_name, data.style, data.trade_name]
          .filter((part) => part)
          .join(' ');

        openPicker(data.product_uuid, name, data.options);
      })
      .catch((err) => {
        setPickerError(
          err?.response?.status === 404
            ? "That product isn't in this showroom's catalog."
            : "Couldn't load that product. Try scanning again."
        );
      })
      .finally(() => {
        setIsLookingUp(false);
      });
  };

  const togglePickerOption = (key) => {
    setPickerChecked((current) => {
      const next = { ...current };

      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }

      return next;
    });
  };

  // Rows for the picker: the general sample, then every SKU on the code. Deduped by
  // sku because product_sku_vw carries a row per distributor, and flagged with
  // isInCart so an already-taken colour shows ticked and locked instead of hidden —
  // "you already have that one" reads better than a silently shorter list.
  const pickerOptions = () => {
    if (!picker) return [];

    const seen = new Set();
    const rows = [
      {
        key: cartKey(picker.productUUID, null),
        productSkuUUID: null,
        label: 'General Product Sample',
        sku: '',
        discontinued: 0,
      },
    ];

    (picker.options ?? []).forEach((option) => {
      const skuUUID = option.product_sku_uuid;

      if (!skuUUID || seen.has(skuUUID)) return;

      seen.add(skuUUID);
      rows.push({
        key: cartKey(picker.productUUID, skuUUID),
        productSkuUUID: skuUUID,
        label: option.color_name || option.product_sku || 'Unnamed option',
        sku: option.product_sku || '',
        discontinued: option.discontinued,
      });
    });

    return rows.map((row) => ({
      ...row,
      isInCart: cart.some((item) => item.key === row.key),
    }));
  };

  // Cart grouped by product, so several colours off one code read as one thing
  // rather than as unrelated lines. Insertion order is preserved.
  const cartGroups = () => {
    const groups = [];

    cart.forEach((item) => {
      const existing = groups.find((g) => g.productUUID === item.productUUID);

      if (existing) {
        existing.items.push(item);
        return;
      }

      groups.push({
        productUUID: item.productUUID,
        productName: item.productName,
        items: [item],
      });
    });

    return groups;
  };

  const pickerSelectedCount = () =>
    pickerOptions().filter((option) => pickerChecked[option.key] && !option.isInCart)
      .length;

  const addPickedToCart = () => {
    if (!picker) return;

    const additions = pickerOptions()
      .filter((option) => pickerChecked[option.key] && !option.isInCart)
      .map((option) => ({
        key: option.key,
        productUUID: picker.productUUID,
        productSkuUUID: option.productSkuUUID,
        productName: picker.productName,
        colorName: option.label,
      }));

    setCart((current) => [...current, ...additions]);
    setPickerChecked({});
  };

  const handleAddAndScanAgain = () => {
    addPickedToCart();
    openScanner();
  };

  const handleAddAndFinish = () => {
    addPickedToCart();
    closePicker();
  };

  const removeFromCart = (key) => {
    setCart((current) => current.filter((item) => item.key !== key));
  };

  const handleGuestCheckoutSubmit = () => {
    const data = {
      userId: tempUserId,
      checkoutName: checkoutName.name,
      checkoutEmail: checkoutEmail.email,
      checkoutPhone: checkoutPhone.phone,
      contactByEmail: 0,
      contactByPhone: 0,
      samples: cart.map((item) => ({
        productUUID: item.productUUID,
        productSkuUUID: item.productSkuUUID,
      })),
    };

    switch (reminderPreference) {
      case '0':
        data.contactByEmail = 1;
        break;
      case '1':
        data.contactByPhone = 1;
        break;
      case '2':
        data.contactByEmail = 1;
        data.contactByPhone = 1;
        break;
    }

    setIsSubmitting(true);
    guestCheckoutBatch(data)
      .then(() => {
        // Refreshes the existing-customer list so a brand-new guest is selectable
        // on the next checkout without a page reload.
        return getCustomerUserInfo().catch(() => undefined);
      })
      .then(() => {
        setMessageType('success');
        setMessage(
          cart.length === 1
            ? 'Guest checkout successful!'
            : `${cart.length} samples checked out!`
        );
        handleAlertOpen();
        handleClear();
        props?.checkoutStatus();
      })
      .catch((err) => {
        setMessageType('error');
        setMessage(
          err?.response?.data?.message ?? 'Error submitting guest checkout'
        );
        handleAlertOpen();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleClear = () => {
    setIsDisabled(false);
    setCheckoutEmail({});
    setCheckoutName({});
    setCheckoutPhone({});
    setTempName(null);
    setTempEmail(null);
    setTempPhone(null);
    setTempUserId(null);
    setCart([]);
    setReminderPreference('');
    closePicker();
  };

  const handleApproveCheckout = (productId, sampleCheckoutId) => {
    approveCheckout(productId, sampleCheckoutId)
      .then(() => {
        props?.checkoutStatus();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleReturn = (productId, sampleCheckoutId) => {
    checkin(productId, sampleCheckoutId)
      .then(() => {
        props?.checkoutStatus();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleCheckedOut = (productId, sampleCheckoutId) => {
    checkin(productId, sampleCheckoutId)
      .then(() => {
        props?.checkoutStatus();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleCancelCheckout = (sampleCheckoutId) => {
    cancelSampleCheckout(sampleCheckoutId)
      .then(() => {
        props?.checkoutStatus();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const canGuestCheckout = () => {
    if (
      checkoutName.name &&
      (checkoutEmail.email || checkoutPhone.phone) &&
      cart.length > 0 &&
      reminderPreference !== '' &&
      !isSubmitting
    ) {
      return false;
    }
    return true;
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => {
        handleClear();
        props.onClose();
      }}
      maxWidth="xl"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <div id="alert-dialog-title" style={{padding: "15px"}}>
        <Typography variant="h4" style={{display: "inline-block", verticalAlign: "middle", color: "#28C4FC"}}>
          <span className="product__hidden-header">Sample </span>Checkout
        </Typography>
        <div style={{display: "inline-block", verticalAlign: "middle", float: "right"}}>
          <Button
            variant="outlined"
            onClick={() => {
              handleClear();
              props.onClose();
            }}
            className="product__pending-header-button"
            style={{}}
          >
            X
          </Button>
        </div>
      </div>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5} lg={3}>
            <List>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="h5">Guest Checkout</Typography>
                  }
                />
              </ListItem>
              <ListItem style={{display: "block"}}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "1px solid #dfe5e9", paddingBottom: "6px"}}>
                  <Typography style={{marginBottom: "0px", fontWeight: 500}}>
                    <Style style={{fontSize: "18px", verticalAlign: "-3px", marginRight: "6px", color: "#6d7f89"}}/>
                    Samples to check out
                  </Typography>
                  <Typography variant="caption" style={{color: "#6d7f89"}}>
                    {cart.length} selected
                  </Typography>
                </div>

                {cart.length === 0 ? (
                  <Typography
                    variant="body2"
                    style={{color: "#6d7f89", textAlign: "center", padding: "14px 8px", border: "1px dashed #dfe5e9", borderRadius: "4px", margin: "8px 0px"}}
                  >
                    Nothing added yet. Pick the options from this product, or scan another sample.
                  </Typography>
                ) : (
                  <div className="product__checkout-cart" style={{margin: "8px 0px", maxHeight: "210px", overflowY: "auto"}}>
                    {cartGroups().map((group) => (
                      <div key={group.productUUID} style={{border: "1px solid #dfe5e9", borderRadius: "4px", marginBottom: "8px"}}>
                        <div style={{backgroundColor: "#f7fafb", borderBottom: "1px solid #dfe5e9", padding: "6px 10px"}}>
                          <Typography variant="body2" style={{marginBottom: "0px", fontWeight: 500}}>
                            {group.productName || 'This product'}
                          </Typography>
                        </div>
                        {group.items.map((item) => (
                          <div key={item.key} style={{display: "flex", alignItems: "center", gap: "8px", padding: "5px 6px 5px 10px"}}>
                            <Typography variant="body2" style={{flex: 1, marginBottom: "0px", minWidth: 0}}>
                              {item.colorName}
                            </Typography>
                            <IconButton
                              size="small"
                              aria-label={`Remove ${item.colorName}`}
                              onClick={() => removeFromCart(item.key)}
                            >
                              <Close style={{fontSize: "16px"}}/>
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{display: "flex", gap: "8px", marginTop: "8px"}}>
                  <Button
                    variant="contained"
                    size="small"
                    style={{flex: 1, backgroundColor: "#142e3e", color: "#28c4fc"}}
                    startIcon={<CropFree/>}
                    onClick={openScanner}
                  >
                    Scan a sample
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={openPickerForThisProduct}
                  >
                    This product
                  </Button>
                </div>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <Autocomplete
                  disabled={isDisabled}
                  freeSolo
                  fullWidth
                  value={tempName}
                  options={props.users}
                  getOptionLabel={(option) =>
                    option.name !== null ? option.name : ''
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Name"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                  onInputChange={(e, data) => handleCheckoutName(data)}
                  onChange={handleNameAutoComplete}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <Autocomplete
                  disabled={isDisabled}
                  freeSolo
                  fullWidth
                  value={tempEmail}
                  options={props.users}
                  getOptionLabel={(option) =>
                    option.email !== null ? option.email : ''
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Email"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                  onInputChange={(e, data) => handleCheckoutEmail(data)}
                  onChange={handleEmailAutoComplete}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Phone />
                </ListItemIcon>
                <Autocomplete
                  disabled={isDisabled}
                  freeSolo
                  fullWidth
                  value={tempPhone}
                  options={props.users}
                  getOptionLabel={(option) =>
                    option.phone !== null ? option.phone : ''
                  }
                  onInputChange={(e, data) => handleCheckoutPhone(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Phone"
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                  onChange={handlePhoneAutoComplete}
                />
              </ListItem>
              <ListItem>
                <FormControl component="fieldset">
                  <FormLabel component="legend"><Typography>Reminder Preference</Typography></FormLabel>
                  <RadioGroup aria-label="Reminder Preference" name="reminder-preference" row value={reminderPreference} onChange={(e) => setReminderPreference(e.target.value)}>
                    <FormControlLabel value="0" control={<Radio />} disabled={!tempEmail?.email && !checkoutEmail?.email} label="Email" labelPlacement='end'/>
                    <FormControlLabel value="1" control={<Radio />} disabled={!tempPhone?.phone && !checkoutPhone?.phone} label="Phone" labelPlacement='end'/>
                    <FormControlLabel value="2" control={<Radio />} disabled={(!tempEmail?.email || !tempPhone?.phone) && (!checkoutEmail?.email || !checkoutPhone?.phone)} label="Both" labelPlacement='end'/>
                  </RadioGroup>
                </FormControl>
              </ListItem>
              <ListItem>
                <Button
                  variant="contained"
                  autoFocus
                  style={{ margin: '10px 0px', marginRight: '20px' }}
                  onClick={handleClear}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  style={{
                    margin: '10px 0px',
                    backgroundColor: '#4caf50',
                    color: '#FFFFFF',
                  }}
                  onClick={handleGuestCheckoutSubmit}
                  disabled={canGuestCheckout()}
                >
                  {isSubmitting
                    ? 'Checking out…'
                    : cart.length > 1
                      ? `Check out ${cart.length} samples`
                      : 'Submit'}
                </Button>
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={7} lg={9} className="product__table-grid">
            <Typography variant="h5" style={{margin: "15px 0px"}}>Sample Checkouts</Typography>
            <TableContainer className="product__table-container">
              <Table aria-label="sample-table" stickyHeader>
                <TableHead>
                  <TableRow>
                    {header.map((column, index) => (
                      <TableCell key={index}>
                        <Typography className={classes.tableHeader}>
                          {column}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.checkoutList.length > 0
                    ? props.checkoutList.map((row, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.location_name}</TableCell>
                            <TableCell>{row.color_name}</TableCell>
                            <TableCell>{row.status_name}</TableCell>
                            <TableCell>{row.days_checked_out}</TableCell>
                            <TableCell>
                              {row.status_name === 'Pending' ? (
                                <Button
                                  className="product__icon-buttons"
                                  onClick={() =>
                                    handleApproveCheckout(
                                      props.productId,
                                      row.sample_checkout_id
                                    )
                                  }
                                >
                                  <Chip
                                    icon={
                                      <LibraryAddCheck
                                        style={{ color: '#EED202' }}
                                      />
                                    }
                                    label={row.status_name}
                                    variant="outlined"
                                    onDelete={() => {
                                      handleCancelCheckout(
                                        row.sample_checkout_id
                                      );
                                    }}
                                  />
                                </Button>
                              ) : row.status_name === 'Returned' ? (
                                <Button
                                  className="product__icon-buttons"
                                  onClick={() =>
                                    handleReturn(
                                      props.productId,
                                      row.sample_checkout_id
                                    )
                                  }
                                >
                                  <Chip
                                    icon={
                                      <LibraryAddCheck
                                        style={{ color: '#EED202' }}
                                      />
                                    }
                                    label={row.status_name}
                                    variant="outlined"
                                  />
                                </Button>
                              ) : row.status_name === 'Checked Out' ? (
                                <Button
                                  className="product__icon-buttons"
                                  onClick={() =>
                                    handleCheckedOut(
                                      props.productId,
                                      row.sample_checkout_id
                                    )
                                  }
                                >
                                  <Chip
                                    icon={
                                      <LibraryAddCheck
                                        style={{ color: '#2db83d' }}
                                      />
                                    }
                                    label={row.status_name}
                                    variant="outlined"
                                  />
                                </Button>
                              ) : (
                                <></>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : <></>}
                </TableBody>
              </Table>
            </TableContainer>

            <div className="product__list-container">
              {props.checkoutList?.map((row, index) => {
                  return (
                    <Accordion key={row.sample_checkout_id + '-mobile'} style={{margin: "1em auto"}}>
                      <AccordionSummary
                        expandIcon={<ExpandMore style={{color: "#28c4fc"}}/>}
                        aria-controls="sample-content"
                        id="sample-header"
                        style={{backgroundColor: "#142e3e", color: "#28c4fc"}}
                      >
                        <Typography style={{marginBottom: "0px"}}>
                          <b>{row.name}</b>
                          <br></br>
                          {row.color_name ?? 'General Product Sample'}
                        </Typography>
                        <Typography style={{marginBottom: "0px"}}></Typography>
                      </AccordionSummary>
                      <AccordionDetails style={{display: "block", backgroundColor: "rgba(0, 0, 0, 0.05)"}}>
                        <Typography style={{marginBottom: "0px"}}><b>{row.name}</b></Typography>
                        <Typography>{row.location_name}</Typography>
                        <Typography>Color/Option: {row.color_name ?? 'General Product Sample'}</Typography>
                        <Typography>Status: {row.status_name}</Typography>
                        <Typography>Location: {row.location_name ?? ''}</Typography>
                        {row.status_name === 'Pending' ? (
                          <Button
                            className="product__icon-buttons"
                            style={{padding: "0px", margin: "1em 0em 0em"}}
                            onClick={() =>
                              handleApproveCheckout(
                                props.productId,
                                row.sample_checkout_id
                              )
                            }
                          >
                            <Chip
                              icon={
                                <LibraryAddCheck
                                  style={{ color: '#EED202' }}
                                />
                              }
                              label={row.status_name}
                              variant="outlined"
                              onDelete={() => {
                                handleCancelCheckout(
                                  row.sample_checkout_id
                                );
                              }}
                            />
                          </Button>
                        ) : row.status_name === 'Returned' ? (
                          <Button
                            className="product__icon-buttons"
                            style={{padding: "0px", margin: "1em 0em 0em"}}
                            onClick={() =>
                              handleReturn(
                                props.productId,
                                row.sample_checkout_id
                              )
                            }
                          >
                            <Chip
                              icon={
                                <LibraryAddCheck
                                  style={{ color: '#EED202' }}
                                />
                              }
                              label={row.status_name}
                              variant="outlined"
                            />
                          </Button>
                        ) : row.status_name === 'Checked Out' ? (
                          <Button
                            className="product__icon-buttons"
                            style={{padding: "0px", margin: "1em 0em 0em"}}
                            onClick={() =>
                              handleCheckedOut(
                                props.productId,
                                row.sample_checkout_id
                              )
                            }
                          >
                            <Chip
                              icon={
                                <LibraryAddCheck
                                  style={{ color: '#2db83d' }}
                                />
                              }
                              label={row.status_name}
                              variant="outlined"
                            />
                          </Button>
                        ) : (
                          <></>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )
                })
              }
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Scan-and-pick overlay. The camera and the SKU list are two stages of one
          dialog so the customer already captured behind it is never lost. */}
      <Dialog
        open={isPickerOpen}
        onClose={closePicker}
        maxWidth="xs"
        fullWidth
        aria-labelledby="sample-picker-title"
      >
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#142e3e", color: "#28c4fc", padding: "10px 14px"}}>
          <Typography id="sample-picker-title" variant="h6" style={{marginBottom: "0px"}}>
            {isScanning ? 'Scan a sample' : 'Add samples'}
          </Typography>
          <Button variant="contained" size="small" onClick={closePicker}>
            <Close style={{fontSize: "18px"}}/>
          </Button>
        </div>

        {isScanning ? (
          <Scanner
            isOpen={isPickerOpen && isScanning}
            onScan={handleScan}
            handleOnClose={closePicker}
            hideHeader
          />
        ) : undefined}

        {isLookingUp ? (
          <div style={{padding: "28px", textAlign: "center"}}>
            <CircularProgress size={28}/>
            <Typography variant="body2" style={{marginTop: "12px", color: "#6d7f89"}}>
              Looking up that product…
            </Typography>
          </div>
        ) : undefined}

        {pickerError ? (
          <div style={{padding: "18px 16px"}}>
            <Typography variant="body2" color="error" style={{marginBottom: "12px"}}>
              {pickerError}
            </Typography>
            <Button variant="outlined" fullWidth onClick={openScanner}>
              Scan again
            </Button>
          </div>
        ) : undefined}

        {picker && !isScanning && !isLookingUp ? (
          <div style={{padding: "14px 16px"}}>
            <Typography style={{marginBottom: "2px", fontWeight: 500}}>
              {picker.productName || 'This product'}
            </Typography>
            <Typography variant="caption" style={{color: "#6d7f89"}}>
              Tick every sample the customer is taking
            </Typography>

            <div style={{border: "1px solid #dfe5e9", borderRadius: "4px", maxHeight: "260px", overflowY: "auto", margin: "10px 0px"}}>
              {pickerOptions().map((option) => (
                <div
                  key={option.key}
                  style={{display: "flex", alignItems: "center", borderBottom: "1px solid #eef2f4", backgroundColor: option.isInCart ? "#f4fafd" : undefined}}
                >
                  <FormControlLabel
                    style={{flex: 1, margin: 0, padding: "2px 8px"}}
                    control={
                      <Checkbox
                        checked={option.isInCart || !!pickerChecked[option.key]}
                        disabled={option.isInCart}
                        onChange={() => togglePickerOption(option.key)}
                        size="small"
                      />
                    }
                    label={
                      <span>
                        <Typography variant="body2" component="span">
                          {option.label}
                        </Typography>
                        {option.discontinued ? (
                          <Tooltip title="Product discontinued">
                            <Info color="secondary" style={{fontSize: "16px", marginLeft: "5px", verticalAlign: "-3px"}}/>
                          </Tooltip>
                        ) : undefined}
                        {option.isInCart ? (
                          <Typography variant="caption" style={{display: "block", color: "#6d7f89"}}>
                            already added
                          </Typography>
                        ) : undefined}
                      </span>
                    }
                  />
                </div>
              ))}
            </div>

            <div style={{display: "flex", gap: "8px"}}>
              <Button
                variant="outlined"
                style={{flex: 1}}
                onClick={handleAddAndScanAgain}
              >
                {pickerSelectedCount() > 0
                  ? `Add ${pickerSelectedCount()} & scan another`
                  : 'Scan another'}
              </Button>
              <Button
                variant="contained"
                style={{backgroundColor: pickerSelectedCount() > 0 ? "#4caf50" : undefined, color: pickerSelectedCount() > 0 ? "#FFFFFF" : undefined}}
                onClick={handleAddAndFinish}
              >
                {pickerSelectedCount() > 0
                  ? `Add ${pickerSelectedCount()} & finish`
                  : 'Done'}
              </Button>
            </div>
          </div>
        ) : undefined}
      </Dialog>
    </Dialog>
  );
};